import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import AIChatbot from './AIChatbot';
import UpdateNotificationBanner, { VersionBadge } from './UpdateNotificationBanner';
import brandingConfig, { getBrandName, getTagline, isModuleEnabled } from '../config/branding';
import { 
  LayoutDashboard, 
  Settings,
  Store,
  LogOut,
  Search,
  Bell,
  Sparkles,
  Users,
  ChevronDown,
  Receipt,
  Wallet,
  BarChart3,
  UserCog,
  Users2,
  Clock,
  DollarSign,
  Settings as SettingsIcon,
  Truck,
  Package,
  ShoppingCart,
  PieChart,
  Activity,
  ScanBarcode,
  Shield,
  Zap,
  Droplets,
  Fuel
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { ThemeToggle } from './ThemeToggle';
import { useSettings } from '../context/SettingsContext';
import { LogoBrain, FlowFeedLogo } from './Logo';
import { useModuleAccess } from '../context/ModuleAccessContext';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { canAccessModule, plan_name, isAdmin } = useModuleAccess();
  const isRTL = i18n.language === 'ar';
  const location = useLocation();
  
  // All dropdowns closed by default for cleaner UI
  const [salesExpanded, setSalesExpanded] = useState(false);
  const [inventoryExpanded, setInventoryExpanded] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(false);
  const [staffExpanded, setStaffExpanded] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  
  // Check if user is admin - Admin sees ALL modules
  const isSuperAdmin = isAdmin;
  
  // Check if a module is enabled based on env and subscription
  // Admin users bypass subscription checks and see ALL modules
  const isModuleEnabled = (moduleId) => {
    // Admin users can access ALL modules
    if (isSuperAdmin) return true;
    
    // First check environment variable (for white-label)
    const envKey = `REACT_APP_MODULE_${moduleId.toUpperCase()}`;
    const envValue = process.env[envKey];
    if (envValue === 'false') return false;
    
    // Then check subscription access
    return canAccessModule(moduleId);
  };
  
  // Reorganized nav items - RETAIL POS FOCUSED
  const navItems = [
    // 1. Dashboard - Retail Overview
    { icon: LayoutDashboard, labelKey: 'sidebar.overview', path: '/dashboard', moduleId: 'dashboard' },
    
    // 1.5 Multi-Store Management
    { icon: Store, label: 'Stores', path: '/stores' },
    
    // 2. Sales & Transactions
    { 
      icon: ShoppingCart, 
      labelKey: 'sidebar.sales', 
      moduleId: 'pos',
      isGroup: true,
      expanded: salesExpanded,
      onToggle: () => setSalesExpanded(!salesExpanded),
      children: [
        { icon: Receipt, labelKey: 'sidebar.transactions', path: '/invoices' },
        { icon: Wallet, labelKey: 'sidebar.expenses', path: '/expenses' },
      ]
    },
    
    // 3. Products & Inventory
    { 
      icon: Package, 
      labelKey: 'sidebar.inventory', 
      moduleId: 'inventory',
      isGroup: true,
      expanded: inventoryExpanded,
      onToggle: () => setInventoryExpanded(!inventoryExpanded),
      children: [
        { icon: Package, labelKey: 'sidebar.products', path: '/products' },
        { icon: Truck, labelKey: 'sidebar.suppliers', path: '/suppliers' },
        { icon: ShoppingCart, labelKey: 'sidebar.purchaseOrders', path: '/purchase-orders' },
        { icon: Package, label: 'Transfers', path: '/inventory-transfers' },
        { icon: Droplets, label: 'Tank Readings', path: '/tank-readings' },
        { icon: Fuel, label: 'Pump Monitor', path: '/pump-monitor' },
      ]
    },
    
    // 4. Reports & Analytics
    { 
      icon: BarChart3, 
      labelKey: 'sidebar.reports', 
      moduleId: 'reports',
      isGroup: true,
      expanded: reportsExpanded,
      onToggle: () => setReportsExpanded(!reportsExpanded),
      children: [
        { icon: BarChart3, labelKey: 'sidebar.analytics', path: '/reports' },
        { icon: Store, label: 'Store Analytics', path: '/store-analytics' },
        { icon: Shield, label: 'Compliance', path: '/compliance' },
        { icon: PieChart, labelKey: 'sidebar.financialReports', path: '/financial-reports' },
        ...(isSuperAdmin ? [{ icon: Activity, labelKey: 'sidebar.systemStatus', path: '/system-status' }] : []),
      ]
    },
    
    // 5. Staff Management (simplified HR)
    ...(isModuleEnabled('hr') ? [{ 
      icon: Users2, 
      labelKey: 'sidebar.staff', 
      moduleId: 'hr',
      isGroup: true,
      expanded: staffExpanded,
      onToggle: () => setStaffExpanded(!staffExpanded),
      children: [
        { icon: Users2, labelKey: 'sidebar.employees', path: '/employees' },
        { icon: Clock, labelKey: 'sidebar.attendance', path: '/attendance' },
        { icon: DollarSign, labelKey: 'sidebar.payroll', path: '/payroll' },
      ]
    }] : []),
    
    // 6. Settings
    { 
      icon: SettingsIcon, 
      labelKey: 'sidebar.settings', 
      isGroup: true,
      expanded: settingsExpanded,
      onToggle: () => setSettingsExpanded(!settingsExpanded),
      children: [
        { icon: SettingsIcon, labelKey: 'sidebar.generalSettings', path: '/settings' },
        { icon: UserCog, labelKey: 'sidebar.userManagement', path: '/users' },
        ...(isModuleEnabled('pos') ? [{ icon: ScanBarcode, labelKey: 'sidebar.posUsers', path: '/pos-users' }] : []),
        ...(isSuperAdmin ? [{ icon: Shield, labelKey: 'sidebar.masterAdmin', path: '/master-admin' }] : []),
        ...(isSuperAdmin ? [{ icon: Sparkles, label: 'Featured (Investors)', path: '/featured' }] : []),
      ]
    },
  ];

  return (
    <div className={`flex h-screen bg-background overflow-hidden ${isRTL ? 'flex-row-reverse' : ''}`}>
      {/* AI-Inspired Sidebar - Theme Aware */}
      <aside className={`w-64 bg-card border-border hidden md:flex flex-col relative overflow-hidden ${isRTL ? 'border-l' : 'border-r'}`}>
        {/* Animated background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -left-24 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 -right-24 w-48 h-48 bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute -bottom-24 left-1/2 w-48 h-48 bg-emerald-500/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
          {/* Circuit pattern overlay */}
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        {/* Logo Section */}
        <div className="p-5 border-b border-border relative z-10">
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <FlowFeedLogo size="default" showTagline={true} />
            <VersionBadge />
          </div>
        </div>

        {/* Quick POS Access Button */}
        <div className="px-3 pt-3 pb-2 relative z-20">
          <Link to="/pos">
            <Button 
              className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg shadow-emerald-500/25 gap-2"
              data-testid="back-to-pos-btn"
            >
              <Store className="h-4 w-4" />
              {isRTL ? 'العودة للكاشير' : 'Back to POS'}
            </Button>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto relative z-10 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
          {navItems.map((item, idx) => {
            const Icon = item.icon;
            const isActive = item.path && location.pathname === item.path;
            const hasActiveChild = item.children?.some(child => location.pathname === child.path);
            
            if (item.isGroup) {
              return (
                <div key={idx} className="mb-1">
                  <button
                    onClick={item.onToggle}
                    className={cn(
                      `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`,
                      hasActiveChild 
                        ? "bg-gradient-to-r from-blue-500/20 to-purple-500/10 text-foreground" 
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    )}
                  >
                    <div className={cn(
                      "p-1.5 rounded-md transition-all",
                      hasActiveChild 
                        ? "bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/20" 
                        : "bg-muted group-hover:bg-muted-foreground/20"
                    )}>
                      <Icon size={16} className={hasActiveChild ? "text-white" : "text-muted-foreground group-hover:text-foreground"} />
                    </div>
                    <span className="font-medium text-sm flex-1">{item.label || t(item.labelKey)}</span>
                    {item.badge && (
                      <span className="text-[9px] font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white px-1.5 py-0.5 rounded shadow-sm shadow-blue-500/30 tracking-wider">
                        {item.badge}
                      </span>
                    )}
                    <ChevronDown size={14} className={cn(
                      "transition-transform duration-200",
                      item.expanded ? "rotate-180" : "",
                      hasActiveChild ? "text-blue-400" : "text-slate-500"
                    )} />
                  </button>
                  
                  {/* Dropdown children with smooth animation */}
                  <div className={cn(
                    "overflow-hidden transition-all duration-300 ease-in-out",
                    item.expanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  )}>
                    <div className={`mt-1 space-y-0.5 ${isRTL ? 'pr-4 border-r-2 mr-5' : 'pl-4 border-l-2 ml-5'} border-border`}>
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = location.pathname === child.path;
                        return (
                          <Link key={child.path} to={child.path}>
                            <div className={cn(
                              `flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-200 ${isRTL ? 'flex-row-reverse' : ''}`,
                              childActive 
                                ? "bg-blue-500/10 text-blue-600 border-l-2 border-blue-500 -ml-[2px]" 
                                : "text-muted-foreground hover:text-foreground hover:bg-accent"
                            )}>
                              <ChildIcon size={14} />
                              <span className="text-sm">{child.label || t(child.labelKey)}</span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            // Regular nav item
            const handleClick = item.openNewWindow 
              ? () => window.open(item.path, '_blank')
              : undefined;

            return (
              <Link 
                key={item.path} 
                to={item.path}
                onClick={handleClick}
                target={item.openNewWindow ? "_blank" : undefined}
              >
                <div className={cn(
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${isRTL ? 'flex-row-reverse' : ''}`,
                  isActive 
                    ? "bg-gradient-to-r from-blue-500/20 to-purple-500/10 text-foreground shadow-lg shadow-blue-500/5" 
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )}>
                  <div className={cn(
                    "p-1.5 rounded-md transition-all",
                    isActive 
                      ? "bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/20" 
                      : "bg-muted group-hover:bg-muted-foreground/20"
                  )}>
                    <Icon size={16} className={isActive ? "text-white" : "text-muted-foreground group-hover:text-foreground"} />
                  </div>
                  <span className="font-medium text-sm flex-1">{item.label || t(item.labelKey)}</span>
                  {item.badge && (
                    <span className="text-[9px] font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white px-1.5 py-0.5 rounded shadow-sm shadow-blue-500/30 tracking-wider">
                      {item.badge}
                    </span>
                  )}
                  {isActive && <div className={`w-1.5 h-1.5 rounded-full bg-blue-500 ${isRTL ? 'mr-auto' : 'ml-auto'} animate-pulse`} />}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="p-4 border-t border-border relative z-10">
          {/* AI Status Card */}
          <div className="bg-muted/50 rounded-xl p-3 border border-border mb-4 backdrop-blur-sm">
            <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-1 rounded-md bg-gradient-to-br from-emerald-500 to-teal-600">
                <Zap size={12} className="text-white" />
              </div>
              <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">{t('sidebar.universalKeyActive')}</span>
            </div>
            <p className="text-[10px] text-muted-foreground">{t('sidebar.gptReady')}</p>
          </div>
          
          {/* User Profile */}
          <button 
            onClick={logout}
            className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-accent transition-all duration-200 group ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
          >
            <div className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full opacity-60 blur-sm"></div>
              <div className="relative h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm ring-2 ring-background">
                {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-foreground">{user?.full_name || 'User'}</p>
              <p className="text-[10px] text-muted-foreground truncate">{user?.email || ''}</p>
            </div>
            <LogOut className="h-4 w-4 text-muted-foreground group-hover:text-red-500 transition-colors" />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-background">
        {/* Update Notification Banner - Shows when updates are available */}
        <UpdateNotificationBanner />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 pointer-events-none bg-grid-pattern opacity-[0.3]"></div>
        
        {/* Header - Removed New Tender Button */}
        <header className={`h-14 border-b border-border flex items-center px-6 justify-between bg-background/80 backdrop-blur-md z-40 sticky top-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="md:hidden">Menu</div>
          <div className={`flex items-center gap-4 flex-1 max-w-xl ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="relative w-full group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full opacity-0 group-hover:opacity-100 transition duration-500 blur"></div>
              <div className="relative flex items-center">
                <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} text-muted-foreground h-4 w-4`} />
                <input 
                  type="text" 
                  placeholder={t('sidebar.searchPlaceholder')}
                  className={`w-full bg-background border border-border rounded-full py-1.5 ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all text-foreground placeholder:text-muted-foreground/70`}
                />
              </div>
            </div>
          </div>
          <div className={`flex items-center gap-3 ${isRTL ? 'mr-6' : 'ml-6'}`}>
            <ThemeToggle />
            <Button variant="ghost" size="icon" className="rounded-full relative hover:bg-accent">
              <Bell className="h-5 w-5 text-muted-foreground" />
              <span className="absolute top-2 right-2 h-2 w-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse"></span>
            </Button>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 lg:p-10 scroll-smooth">
          <div className="max-w-7xl mx-auto">
             {children}
          </div>
        </div>

        {/* AI Chatbot for Help */}
        <AIChatbot variant="dashboard" />
      </main>
    </div>
  );
};

export default Layout;
